import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Clock, Eye, ChevronRight, Pin, AlertCircle, Send, Tag, Users, TrendingUp, Shield, Lock, X, Flame, ArrowRight } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const CAT_CONFIG = {
  'general': { label: 'Général', color: '#64748b', bg: '#f1f5f9', ring: '#cbd5e1' },
  'cas-cliniques': { label: 'Cas Cliniques', color: '#2563eb', bg: '#eff6ff', ring: '#93c5fd' },
  'lentilles': { label: 'Lentilles', color: '#059669', bg: '#ecfdf5', ring: '#6ee7b7' },
  'équipement': { label: 'Équipement', color: '#d97706', bg: '#fffbeb', ring: '#fcd34d' },
  'ortho-k': { label: 'Orthokératologie', color: '#7c3aed', bg: '#f5f3ff', ring: '#c4b5fd' },
  'pediatrie': { label: 'Pédiatrie', color: '#e11d48', bg: '#fff1f2', ring: '#fda4af' },
  'autre': { label: 'Autre', color: '#0891b2', bg: '#ecfeff', ring: '#67e8f9' },
};

const timeAgo = (dateStr) => {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const ForumPage = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [filterCat, setFilterCat] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const hasAccess = () => {
    if (!user || !token) return false;
    const role = user.role;
    if (['super_admin', 'admin', 'regional', 'auteur', 'tresorier'].includes(role)) return true;
    if (role === 'ophthalmologist' && user.subscription_status === 'active') return true;
    if (role === 'intern' && user.approval_status === 'approved') return true;
    return false;
  };

  useEffect(() => {
    if (!hasAccess()) {
      setLoading(false);
      setAccessDenied(true);
      return;
    }
    fetchTopics();
  }, [filterCat]);

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const params = filterCat ? `?category=${filterCat}` : '';
      const res = await axios.get(`${API}/forum/topics${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setTopics(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Erreur de chargement');
      }
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || !newContent.trim()) { toast.error('Titre et contenu requis'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/topics`, { title: newTitle, content: newContent, category: newCategory }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sujet soumis ! Il sera visible apres moderation.');
      setNewTitle(''); setNewContent(''); setNewCategory('general'); setShowNew(false);
      fetchTopics();
    } catch { toast.error('Erreur lors de la creation'); }
    setSubmitting(false);
  };

  const pinnedTopics = topics.filter(t => t.pinned);
  const regularTopics = topics.filter(t => !t.pinned);

  // ===== ACCESS DENIED =====
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="forum-page">
        <SiteHeader />
        <SEO title="Forum SFOALC" />
        <div className="flex items-center justify-center px-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center shadow-lg shadow-blue-900/20">
              <Lock size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="access-denied-title">
              Forum réservé aux adhérents
            </h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-8">
              Le forum d'entraide est un espace prive réservé aux ophtalmologistes membres de la SFOALC avec une adhesion active.
            </p>
            {!token ? (
              <div className="space-y-3">
                <button onClick={() => navigate('/auth')} className="w-full px-6 py-3 bg-[#35AFE4] text-white text-sm font-bold rounded-xl hover:bg-[#1e3a5f] transition-all shadow-md shadow-blue-900/10" data-testid="login-cta">
                  Se connecter
                </button>
                <button onClick={() => navigate('/adhesion')} className="w-full px-6 py-3 border-2 border-[#35AFE4] text-[#35AFE4] text-sm font-semibold rounded-xl hover:bg-[#eaf6fc] transition-colors" data-testid="join-cta">
                  Devenir membre
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                  Votre adhésion n'est pas active. Complétez votre cotisation pour accéder au forum.
                </p>
                <button onClick={() => navigate('/adhesion')} className="w-full px-6 py-3 bg-[#35AFE4] text-white text-sm font-bold rounded-xl hover:bg-[#1e3a5f] transition-all" data-testid="subscribe-cta">
                  Renouveler mon adhesion
                </button>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="forum-page">
      <SiteHeader />
      <SEO title="Forum SFOALC" description="Forum d'entraide entre praticiens adhérents" />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2a5577 50%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(58,143,183,0.15) 0%, transparent 70%)', right: '-200px', top: '-200px' }} />
          <div className="absolute w-[400px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(58,143,183,0.1) 0%, transparent 70%)', left: '-100px', bottom: '-100px' }} />
        </div>
        <div className="max-w-5xl mx-auto px-4 pt-14 pb-16 relative z-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white/80 text-[11px] font-semibold rounded-full mb-5 border border-white/10">
                <Shield size={12} /> Réservé aux membres adhérents
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="forum-title">
                Forum d'entraide
              </h1>
              <p className="text-white/50 text-sm max-w-md leading-relaxed">
                Echangez entre praticiens sur vos cas cliniques, équipements et pratiques. Tous les sujets et réponses sont moderes par l'équipe SFOALC.
              </p>
            </div>
            <div className="flex gap-3">
              <div className="bg-white/[0.08] backdrop-blur-sm rounded-2xl border border-white/10 px-5 py-4 text-center min-w-[90px]">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow', sans-serif" }}>{topics.length}</p>
                <p className="text-[10px] text-white/40 mt-0.5 font-medium">Sujets</p>
              </div>
              <div className="bg-white/[0.08] backdrop-blur-sm rounded-2xl border border-white/10 px-5 py-4 text-center min-w-[90px]">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow', sans-serif" }}>{topics.reduce((acc, t) => acc + (t.replies_count || 0), 0)}</p>
                <p className="text-[10px] text-white/40 mt-0.5 font-medium">Réponses</p>
              </div>
            </div>
          </div>
        </div>
        <svg viewBox="0 0 1440 50" preserveAspectRatio="none" className="block w-full" style={{ height: '35px' }}>
          <path fill="#f8fafb" d="M0,50 L0,25 Q360,0 720,20 Q1080,40 1440,10 L1440,50 Z" />
        </svg>
      </section>

      {/* ===== CONTENT ===== */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ===== MAIN ===== */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setFilterCat('')}
                  className={`px-3.5 py-1.5 text-[11px] font-semibold rounded-lg transition-all duration-200 ${!filterCat ? 'bg-[#1e3a5f] text-white shadow-md shadow-blue-900/15' : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-100'}`}
                  data-testid="filter-all">Tous</button>
                {Object.entries(CAT_CONFIG).map(([key, cat]) => (
                  <button key={key} onClick={() => setFilterCat(key)}
                    className={`px-3.5 py-1.5 text-[11px] font-semibold rounded-lg transition-all duration-200 ${filterCat === key ? 'text-white shadow-md' : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-100'}`}
                    style={filterCat === key ? { background: cat.color, boxShadow: `0 4px 12px ${cat.color}30` } : {}}
                    data-testid={`filter-${key}`}>{cat.label}</button>
                ))}
              </div>
              <button onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] text-white text-xs font-bold rounded-xl hover:bg-[#1e9bd4] transition-all shadow-md shadow-blue-900/15 hover:shadow-lg hover:-translate-y-0.5"
                data-testid="new-topic-btn">
                <Plus size={15} strokeWidth={2.5} /> Nouveau sujet
              </button>
            </div>

            {/* New topic form - modal overlay */}
            {showNew && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-100 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="new-topic-form">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4]">
                    <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Barlow', sans-serif" }}>Nouveau sujet</h3>
                    <button onClick={() => setShowNew(false)} className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Titre</label>
                      <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
                        placeholder="Ex: Adaptation lentille rigide sur keratocone"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] focus:bg-white outline-none transition-all"
                        data-testid="topic-title-input" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Catégorie</label>
                      <select value={newCategory} onChange={e => setNewCategory(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none text-gray-700 focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] focus:bg-white transition-all"
                        data-testid="topic-category-select">
                        {Object.entries(CAT_CONFIG).map(([key, cat]) => (
                          <option key={key} value={key}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Contenu</label>
                      <textarea value={newContent} onChange={e => setNewContent(e.target.value)}
                        placeholder="Décrivez votre question ou cas clinique en detail..."
                        rows={5}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] focus:bg-white transition-all"
                        data-testid="topic-content-input" />
                    </div>
                  </div>
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <p className="text-[10px] text-gray-400 flex items-center gap-1.5"><Shield size={11} className="text-[#35AFE4]" /> Soumis à modération avant publication</p>
                    <div className="flex gap-2">
                      <button onClick={() => setShowNew(false)} className="px-4 py-2.5 text-xs text-gray-500 font-medium hover:text-gray-700 transition-colors">Annuler</button>
                      <button onClick={handleCreate} disabled={submitting || !newTitle.trim() || !newContent.trim()}
                        className="px-6 py-2.5 bg-[#35AFE4] text-white text-xs font-bold rounded-xl hover:bg-[#1e3a5f] disabled:opacity-40 transition-all shadow-sm"
                        data-testid="submit-topic-btn">
                        {submitting ? 'Envoi...' : 'Publier'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pinned topics */}
            {pinnedTopics.length > 0 && (
              <div className="mb-5">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                  <Pin size={10} className="-rotate-45" /> Épinglés
                </p>
                <div className="space-y-2">
                  {pinnedTopics.map((t, i) => (
                    <TopicCard key={t.id} topic={t} index={i} navigate={navigate} pinned />
                  ))}
                </div>
              </div>
            )}

            {/* Topics list */}
            {loading ? (
              <div className="flex flex-col items-center py-20 gap-3">
                <div className="w-8 h-8 border-[2.5px] border-[#35AFE4] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-gray-400">Chargement des discussions...</p>
              </div>
            ) : regularTopics.length === 0 && pinnedTopics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <MessageSquare size={28} className="text-gray-300" />
                </div>
                <p className="text-sm text-gray-400 mb-1">Aucun sujet pour le moment</p>
                <p className="text-[11px] text-gray-300">Soyez le premier à lancer une discussion !</p>
                <button onClick={() => setShowNew(true)} className="mt-5 inline-flex items-center gap-1.5 text-[#35AFE4] text-xs font-semibold hover:underline" data-testid="create-first-topic">
                  <Plus size={13} /> Créer un sujet
                </button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="topics-list">
                {regularTopics.map((t, i) => (
                  <TopicCard key={t.id} topic={t} index={i} navigate={navigate} />
                ))}
              </div>
            )}
          </div>

          {/* ===== SIDEBAR ===== */}
          <div className="lg:w-[260px] flex-shrink-0 space-y-4">
            {/* Categories */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <h3 className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>Catégories</h3>
              <div className="space-y-1">
                {Object.entries(CAT_CONFIG).map(([key, cat]) => {
                  const count = topics.filter(t => t.category === key).length;
                  return (
                    <button key={key} onClick={() => setFilterCat(filterCat === key ? '' : key)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${filterCat === key ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                      style={filterCat === key ? { background: cat.color } : {}}
                      data-testid={`sidebar-cat-${key}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                        {cat.label}
                      </div>
                      {count > 0 && <span className={`text-[10px] font-bold ${filterCat === key ? 'text-white/70' : 'text-gray-300'}`}>{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Rules */}
            <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2a5577] rounded-2xl p-5 text-white shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider mb-3 text-white/90" style={{ fontFamily: "'Barlow', sans-serif" }}>Règles du forum</h3>
              <div className="space-y-2.5 text-[11px] text-white/60 leading-relaxed">
                <p className="flex items-start gap-2"><Shield size={11} className="text-[#35AFE4] mt-0.5 flex-shrink-0" /> Chaque sujet est modéré avant publication</p>
                <p className="flex items-start gap-2"><Users size={11} className="text-[#35AFE4] mt-0.5 flex-shrink-0" /> Restez courtois et professionnel</p>
                <p className="flex items-start gap-2"><AlertCircle size={11} className="text-[#35AFE4] mt-0.5 flex-shrink-0" /> Pas de données patient identifiables</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const TopicCard = ({ topic: t, index: i, navigate, pinned }) => {
  const cat = CAT_CONFIG[t.category] || CAT_CONFIG.general;
  const isHot = (t.replies_count || 0) >= 5;

  return (
    <button
      onClick={() => navigate(`/forum/${t.id}`)}
      className={`w-full text-left bg-white rounded-xl border p-4 sm:p-5 hover:shadow-md transition-all duration-300 flex items-start gap-4 group ${pinned ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100 hover:border-[#35AFE4]/20'}`}
      style={{ animationDelay: `${i * 40}ms` }}
      data-testid={`topic-${i}`}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 shadow-sm shadow-blue-900/10">
        {t.author_name?.slice(0, 2).toUpperCase() || '??'}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {pinned && <Pin size={11} className="text-amber-500 -rotate-45 flex-shrink-0" />}
          {isHot && <Flame size={11} className="text-orange-400 flex-shrink-0" />}
          <h3 className="text-[13px] font-semibold text-gray-900 truncate group-hover:text-[#35AFE4] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {t.title}
          </h3>
        </div>
        <p className="text-[11px] text-gray-400 line-clamp-1 mb-2">{t.content}</p>
        <div className="flex items-center gap-3 text-[10px] text-gray-400">
          <span className="px-2 py-0.5 rounded-md text-[9px] font-bold" style={{ background: cat.bg, color: cat.color }}>
            {cat.label}
          </span>
          <span className="font-medium text-gray-500">{t.author_name}</span>
          <span className="flex items-center gap-0.5"><Clock size={9} /> {timeAgo(t.created_at)}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-5 flex-shrink-0 self-center">
        <div className="text-center hidden sm:block">
          <p className="text-sm font-bold text-gray-800" style={{ fontFamily: "'Barlow', sans-serif" }}>{t.replies_count || 0}</p>
          <p className="text-[9px] text-gray-300">réponses</p>
        </div>
        <div className="text-center hidden sm:block">
          <p className="text-sm font-bold text-gray-400" style={{ fontFamily: "'Barlow', sans-serif" }}>{t.views || 0}</p>
          <p className="text-[9px] text-gray-300">vues</p>
        </div>
        <ChevronRight size={16} className="text-gray-200 group-hover:text-[#35AFE4] group-hover:translate-x-1 transition-all" />
      </div>
    </button>
  );
};

export default ForumPage;
